import React, { useMemo } from 'react';
import { ArrowLeft, ExternalLink, FileText, Sparkles } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import styles from './AIVoiceExtractionResult.module.css';

import type { ExtractionResultRouteState } from './AIVoiceExtraction.types';
import {
  formatLabel,
  getStructuredExtractionData,
  hasMeaningfulValue,
} from './AIVoiceExtraction.api';

const renderValue = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined || value === '') {
    return <span className={styles.emptyValue}>-</span>;
  }

  if (Array.isArray(value)) {
    if (!value.length) return <span className={styles.emptyValue}>-</span>;

    return (
      <ul className={styles.arrayList}>
        {value.map((item, index) => (
          <li key={`array-item-${index}`} className={styles.arrayItem}>
            {typeof item === 'object' && item !== null ? (
              <div className={styles.nestedBox}>{renderValue(item)}</div>
            ) : (
              String(item)
            )}
          </li>
        ))}
      </ul>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).filter(([, entryValue]) =>
      hasMeaningfulValue(entryValue)
    );

    if (!entries.length) return <span className={styles.emptyValue}>-</span>;

    return (
      <div className={styles.nestedGroup}>
        {entries.map(([key, entryValue]) => (
          <div key={key} className={styles.nestedRow}>
            <div className={styles.nestedLabel}>{formatLabel(key)}</div>
            <div className={styles.nestedValue}>{renderValue(entryValue)}</div>
          </div>
        ))}
      </div>
    );
  }

  return <span>{String(value)}</span>;
};

const AIVoiceExtractionResult: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const navState =
    (location.state as ExtractionResultRouteState | null) ?? ({} as ExtractionResultRouteState);

  const structuredData = useMemo(
    () => getStructuredExtractionData(navState.extractionResponse ?? {}),
    [navState.extractionResponse]
  );

  const topLevelSections = useMemo(
    () =>
      Object.entries(structuredData).filter(([, value]) => hasMeaningfulValue(value)),
    [structuredData]
  );

  if (!navState.extractionResponse || !navState.selectedType) {
    return (
      <div className={styles.emptyPage}>
        <div className={styles.emptyCard}>
          <h2 className={styles.emptyTitle}>No extraction result found</h2>
          <p className={styles.emptyText}>
            Please go back to the extraction screen and run AI extraction first.
          </p>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate('/ai/aiHub/voicedataentry/chat/extraction-review')}
          >
            <ArrowLeft size={18} />
            Back to Extraction
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageShell}>
      <div className={styles.topBar}>
        <button
          type="button"
          className={styles.backLinkButton}
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={18} />
          Back
        </button>

        <div className={styles.titleBlock}>
          <h1 className={styles.pageTitle}>AI Extracted Clinical Data</h1>
          <p className={styles.pageSubTitle}>
            Review the structured response generated from the transcript.
          </p>
        </div>

        <button className={styles.secondaryActionButton} type="button">
          <ExternalLink size={16} />
          Open
        </button>
      </div>

      <div className={styles.contentLayout}>
        <div className={styles.leftColumn}>
          <section className={styles.summaryCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitleWrap}>
                <Sparkles size={18} />
                <h2 className={styles.cardTitle}>AI Summary</h2>
              </div>

              <div className={styles.typeBadge}>{navState.selectedType.label}</div>
            </div>

            <div className={styles.summaryLines}>
              {navState.aiSummaryLines.map((line, index) => (
                <p key={`${line}-${index}`} className={styles.summaryLine}>
                  {line}
                </p>
              ))}
            </div>

            <div className={styles.metaGrid}>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Transcription ID</span>
                <span className={styles.metaValue}>
                  {String(navState.extractionResponse.transcription_id ?? '-')}
                </span>
              </div>

              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>EMR ID</span>
                <span className={styles.metaValue}>
                  {String(navState.extractionResponse.emr_id ?? '-')}
                </span>
              </div>
            </div>
          </section>

          <section className={styles.transcriptCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitleWrap}>
                <FileText size={18} />
                <h2 className={styles.cardTitle}>Transcript Reference</h2>
              </div>
            </div>

            <div className={styles.transcriptPreview}>{navState.transcript}</div>
          </section>
        </div>

        <div className={styles.rightColumn}>
          {topLevelSections.length ? (
            topLevelSections.map(([sectionKey, sectionValue]) => (
              <section key={sectionKey} className={styles.sectionCard}>
                <h3 className={styles.sectionTitle}>{formatLabel(sectionKey)}</h3>
                <div className={styles.sectionContent}>{renderValue(sectionValue)}</div>
              </section>
            ))
          ) : (
            <section className={styles.sectionCard}>
              <h3 className={styles.sectionTitle}>Extracted Data</h3>
              <div className={styles.sectionContent}>
                <div className={styles.nestedBox}>{renderValue(structuredData)}</div>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIVoiceExtractionResult;